import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { EditableCell, StatusPill } from '../components/InlineEditTable';
import DataTable from '../components/DataTable';
import { useBulkSelect } from '../hooks/useBulkSelect';
import ProgramFilters from '../components/ProgramFilters';
import { filterPrograms, pmNameOf } from '../utils/programFilters';
import { parseLocalDate } from '../utils/weekDates';
import MultiSelect from '../components/MultiSelect';
import ReparentDeleteModal from '../components/ReparentDeleteModal';
import FormField, { FORM_INPUT_STYLE } from '../components/FormField';
import { useFormValidation } from '../hooks/useFormValidation';
import { useSoftDelete } from '../hooks/useSoftDelete';
import IoPdfCell, { openIoPdf } from '../components/IoPdfCell';
import Toast from '../components/Toast';
import CollapsibleSection from '../components/CollapsibleSection';
import { useCurrentUser } from '../context/UserContext';
import { getLeadStatuses, LEAD_STATUS_COLORS } from '../utils/leadStatus';
import { buildStatusGroupMap, splitByStatusGroup } from '../utils/statusGroups';

const PROGRAM_RULES = {
  program_name: { required: true, label: 'Program Name' },
  client_id: { required: true, label: 'Client' },
  order_date: { required: true, label: 'Order Date' },
  flight_date_start: { required: true, label: 'Flight Start' },
  flight_date_end: { required: true, label: 'Flight End' },
};

const API = process.env.REACT_APP_API_URL;

const STATUS_COLORS = {
  'Pending Kickoff': '#e67e22',
  'Spinup': '#3498db',
  'WIP': '#9b59b6',
  'Completed': '#27ae60',
};

// The segmented view switcher, in display order. (Masked view was retired
// 2026-10-28 — unused; archived in docs/archive/pages/ProgramsMaskedView.md.)
const SWITCHER_VIEWS = [
  ['edit',      'Edit',      'Every program field, inline-editable'],
  ['drilldown', 'Drilldown', 'One program at a time, down to goals and lead counts'],
  ['compact',   'Compact',   'Every program on one screen with its lead counts by status'],
];

// The chosen view rides in the URL (?view=compact) so a reload, Back or a bookmark
// keeps it, and is remembered per browser so arriving from the nav does too. An old
// ?masked=1 link, or anything unrecognized, falls back to Drilldown.
// Drilldown notes: three lines until "Show more".
const NOTES_CLAMP = { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' };

// Drilldown panes never get squeezed below this; the page scrolls instead.
const DL_MIN_HEIGHT = 420;
const DL_BOTTOM_GAP = 0;  // extra breathing room beyond the shell's own padding

const VIEW_KEYS = SWITCHER_VIEWS.map(([v]) => v);
const VIEW_STORAGE_KEY = 'programs_view';
const SIDEBAR_STORAGE_KEY = 'programs_sidebar_collapsed';
function initialView(searchParams) {
  const fromUrl = searchParams.get('view');
  if (VIEW_KEYS.includes(fromUrl)) return fromUrl;
  try {
    const saved = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (VIEW_KEYS.includes(saved)) return saved;
  } catch (e) { /* storage blocked — fall through to the default */ }
  return 'drilldown';
}

const emptyForm = () => ({
  po_number: '', program_name: '', client_id: '', agency_id: '', status: '',
  program_manager_id: '', supporting_manager_ids: [], order_date: '', flight_date_start: '', flight_date_end: '',
  total_revenue: '', leads_ordered: '', payment_terms: '', notes: '',
});


function fmt(date) {
  if (!date) return '—';
  return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtMoney(v) {
  if (!v && v !== 0) return '—';
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Programs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(() => initialView(searchParams));
  const [programs, setPrograms] = useState([]);
  const [deleteModal, setDeleteModal] = useState(null);  // program being deleted-with-children
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPm, setFilterPm] = useState('');  // a main PM's id, PM_NONE, or '' for all
  const [showDeleted, setShowDeleted] = useState(false);
  const navigate = useNavigate();
  // Set by the creation wizard on its way back here. Transient by design — it is
  // gone on the next navigation or reload, same as the old post-import banner.
  const location = useLocation();
  const [created, setCreated] = useState(location.state?.createdProgram || null);
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const segBtn = (active) => ({
    padding: '5px 14px', background: active ? '#2c3e50' : 'transparent',
    color: active ? 'white' : '#555', border: 'none', borderRadius: '4px',
    cursor: 'pointer', fontSize: '13px',
  });
  const isDrilldown = view === 'drilldown';

  // Keep ?view= and the remembered choice in step with the toggle. `replace` so
  // switching views doesn't stack up history entries. Also drops a stale ?masked=1.
  useEffect(() => {
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, view); } catch (e) { /* storage blocked */ }
    if (searchParams.get('view') === view && !searchParams.has('masked')) return;
    const next = new URLSearchParams(searchParams);
    next.set('view', view);
    next.delete('masked');
    setSearchParams(next, { replace: true });
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drilldown state
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [expandedComponents, setExpandedComponents] = useState({});
  const [expandedGoals, setExpandedGoals] = useState({});
  const [leadSummaries, setLeadSummaries] = useState({}); // goal_id → {total, by_status}
  // Compact view: program_id → {total, by_status}. null until the view is opened.
  const [rollup, setRollup] = useState(null);
  const [rollupError, setRollupError] = useState(false);
  const [rollupRefreshing, setRollupRefreshing] = useState(false);
  const [rollupAt, setRollupAt] = useState(null);  // when the counts were last fetched
  const [compactSort, setCompactSort] = useState({ field: 'client', dir: 'asc' });
  // Drilldown sidebar sort. Defaults to status (lifecycle order), which comes from
  // lookup_values.sort_order via the `statuses` array — never a hardcoded status list,
  // since the real values already drift (plain-hyphen "Closed - Not Invoiced" on prod
  // vs. the en-dash the original migration seeded).
  const [dlSort, setDlSort] = useState({ field: 'status', dir: 'asc' });
  const autoSelected = useRef(false);
  // Drilldown panes: the row is sized to the window (below wherever it starts) so
  // the sidebar and the detail each scroll independently.
  const dlRef = useRef(null);
  const dlListRef = useRef(null);
  const dlDetailRef = useRef(null);
  const [dlHeight, setDlHeight] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1'; } catch (e) { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarCollapsed ? '1' : '0'); } catch (e) { /* storage blocked */ }
  }, [sidebarCollapsed]);

  const { selected, toggle, toggleAll, clear } = useBulkSelect();
  const { errors, validate, reset: resetErrors } = useFormValidation(PROGRAM_RULES);

  // Columns: PO#, Program Name, Client, Status, PM, Flight Start, Flight End, Revenue, Actions, [Select]

  const fetchClients = () => axios.get(`${API}/clients/`).then(r => setClients(r.data));

  useEffect(() => {
    fetchClients();
    // Supporting PMs are eligible both as the primary PM and as supporting ones,
    // so one list serves both pickers.
    axios.get(`${API}/users/?role=admin,program_manager,supporting_program_manager`).then(r => setManagers(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setStatuses(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchPrograms(); }, [showDeleted]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPrograms = () => axios.get(`${API}/programs/`, { params: { include_deleted: showDeleted } }).then(r => setPrograms(r.data));

  const { toast, dismissToast, restore: restoreProgram, deleteWithUndo } = useSoftDelete('program', fetchPrograms);

  // Persist a single inline-edited field (list view). The PUT response is already a
  // fully joined ProgramOut (_load_program) — use it directly instead of a full
  // fetchPrograms() refetch of every row for a one-field change.
  const replaceRow = (updated) =>
    setPrograms(prev => prev.map(p => p.id === updated.id ? updated : p));

  const saveField = async (id, patch) => {
    const res = await axios.put(`${API}/programs/${id}`, patch);
    replaceRow(res.data);
  };

  const bulkSetStatus = async (status) => {
    await Promise.all([...selected].map(id => axios.put(`${API}/programs/${id}`, { status })));
    clear();
    await fetchPrograms();
  };

  // Edit only — creating a program goes through the wizard at /programs/new, which
  // is the sole caller of POST /programs/ now.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(form)) return;
    const data = {
      ...form,
      client_id: form.client_id ? parseInt(form.client_id) : null,
      agency_id: form.agency_id ? parseInt(form.agency_id) : null,
      program_manager_id: form.program_manager_id ? parseInt(form.program_manager_id) : null,
      supporting_manager_ids: (form.supporting_manager_ids || []).map(v => parseInt(v)),
      total_revenue: form.total_revenue !== '' ? parseFloat(form.total_revenue) : null,
      leads_ordered: form.leads_ordered !== '' ? parseInt(form.leads_ordered) : null,
      order_date: form.order_date || null,
      flight_date_start: form.flight_date_start || null,
      flight_date_end: form.flight_date_end || null,
    };
    try {
      await axios.put(`${API}/programs/${editing}`, data);
      setEditing(null);
      setForm(emptyForm());
      setShowForm(false);
      resetErrors();
      fetchPrograms();
    } catch (err) {
      const d = err.response?.data?.detail;
      alert('Could not save program: ' + (Array.isArray(d) ? d.map(x => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join('; ') : (d || err.message)));
    }
  };

  const handleEdit = (p) => {
    setEditing(p.id);
    setForm({
      po_number: p.po_number || '',
      program_name: p.program_name || '',
      client_id: p.client_id || '',
      agency_id: p.agency_id || '',
      status: p.status || '',
      program_manager_id: p.program_manager_id || '',
      supporting_manager_ids: (p.supporting_managers || []).map(u => u.id),
      order_date: p.order_date || '',
      flight_date_start: p.flight_date_start || '',
      flight_date_end: p.flight_date_end || '',
      total_revenue: p.total_revenue !== null && p.total_revenue !== undefined ? p.total_revenue : '',
      leads_ordered: p.leads_ordered !== null && p.leads_ordered !== undefined ? p.leads_ordered : '',
      payment_terms: p.payment_terms || '',
      notes: p.notes || '',
    });
    setShowForm(true);
    resetErrors();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (p) => {
    if ((p.components || []).length > 0) {
      setDeleteModal(p);  // has children → 3-way modal (cascade / move / cancel)
      return;
    }
    if (window.confirm('Delete this program?')) {
      deleteWithUndo(p, p.program_name || 'program', () => axios.delete(`${API}/programs/${p.id}`))
        .catch(err => alert('Delete failed: ' + (err.response?.data?.detail || err.message)));
    }
  };

  const doCascadeDelete = async () => {
    setDeleteBusy(true);
    try {
      const p = deleteModal;
      await deleteWithUndo(p, p.program_name || 'program', () => axios.delete(`${API}/programs/${p.id}`));
      setDeleteModal(null);
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    } finally { setDeleteBusy(false); }
  };

  const doReparentDelete = async (moves) => {
    setDeleteBusy(true);
    try {
      const p = deleteModal;
      await deleteWithUndo(p, p.program_name || 'program', () => axios.post(`${API}/programs/${p.id}/delete-with-reparent`, { moves }));
      setDeleteModal(null);
    } catch (err) {
      alert('Move & delete failed: ' + (err.response?.data?.detail || err.message));
    } finally { setDeleteBusy(false); }
  };

  const loadDrilldown = async (prog) => {
    setSelectedProgram(prog);
    setDrilldown(null);
    setExpandedComponents({});
    setExpandedGoals({});
    setLeadSummaries({});
    const res = await axios.get(`${API}/programs/${prog.id}/drilldown`);
    const dd = res.data;
    setDrilldown(dd);
    // Components start expanded on every selection, not just the first — a program
    // whose goals were hidden until clicked was the main thing this view was missing.
    const expanded = {};
    (dd.components || []).forEach(c => { expanded[c.id] = true; });
    setExpandedComponents(expanded);
    // Load lead summaries for the goals in this program — program_id-scoped so
    // the backend aggregates only this program's leads instead of every goal in
    // the system (the largest query this page used to make, on every selection).
    const allGoalIds = (dd.components || []).flatMap(c => (c.goals || []).map(g => g.id));
    if (allGoalIds.length > 0) {
      axios.get(`${API}/leads/summary?program_id=${prog.id}`)
        .then(r => {
          const map = {};
          r.data.forEach(entry => { map[entry.goal_id] = entry; });
          setLeadSummaries(map);
        })
        .catch(() => {});
    }
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || '—';
  const clientSalesPerson = (id) => clients.find(c => c.id === id)?.sales_person || null;

  // Both header filters apply to every view (Edit, Drilldown, Compact), since all
  // three render from this one list.
  const filteredPrograms = filterPrograms(programs, { status: filterStatus, pm: filterPm });
  const anyFilter = Boolean(filterStatus || filterPm);

  // Pending/Running always visible, sorted by flight_date_end desc; Closed collapsed
  // at the bottom — classification comes from lookup_values.status_group (admin-
  // editable), not a hardcoded status list, since real status strings already drift
  // (e.g. "Closed – Not Invoiced" was added ad hoc outside any migration).
  const statusGroupMap = buildStatusGroupMap(statuses);
  const { active: activePrograms, closed: closedPrograms } = splitByStatusGroup(
    filteredPrograms, { statusKey: 'status', groupMap: statusGroupMap, dateKey: 'flight_date_end' }
  );

  // Drilldown sidebar sort. Deliberately applied *after* splitByStatusGroup rather than
  // by changing it — that util is shared with Components/Goals/PM Home/Supplier Portal,
  // and the list view already set this precedent: sorting only reorders within a bucket,
  // it never decides whether Closed collapses.
  const statusRank = (value) => {
    const i = statuses.findIndex(s => s.value === value);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;   // unclassified sorts last, never first
  };
  const dateVal = (d) => (d ? new Date(d).getTime() : null);
  const sortDrilldown = (rows) => {
    const dir = dlSort.dir === 'desc' ? -1 : 1;
    const byOrderDate = (a, b) => {
      const ad = dateVal(a.order_date), bd = dateVal(b.order_date);
      if (ad === bd) return 0;
      if (ad === null) return 1;                     // missing dates sink, either direction
      if (bd === null) return -1;
      return ad - bd;
    };
    const cmp = (a, b) => {
      if (dlSort.field === 'client') {
        const c = clientName(a.client_id).localeCompare(clientName(b.client_id));
        return c !== 0 ? c : (a.program_name || '').localeCompare(b.program_name || '');
      }
      if (dlSort.field === 'order_date') return byOrderDate(a, b);
      if (dlSort.field === 'pm') {
        const an = pmNameOf(a), bn = pmNameOf(b);
        if (an !== bn) {
          if (!an) return 1;                         // no PM sinks, like a missing date
          if (!bn) return -1;
          return an.localeCompare(bn);
        }
        const r = statusRank(a.status) - statusRank(b.status);
        return r !== 0 ? r : byOrderDate(a, b);
      }
      const r = statusRank(a.status) - statusRank(b.status);
      return r !== 0 ? r : byOrderDate(a, b);        // within a status, oldest order first
    };
    return [...rows].sort((a, b) => cmp(a, b) * dir);
  };
  const dlActive = sortDrilldown(activePrograms);
  const dlClosed = sortDrilldown(closedPrograms);

  const toggleDlSort = (field) => setDlSort(prev => (
    prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }
  ));

  const renderSidebarRow = (p) => (
    <div key={p.id} data-program-id={p.id} onClick={() => loadDrilldown(p)}
      style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: selectedProgram?.id === p.id ? '#ebf5fb' : 'white' }}
      onMouseEnter={e => { if (selectedProgram?.id !== p.id) e.currentTarget.style.background = '#f8f9fa'; }}
      onMouseLeave={e => { if (selectedProgram?.id !== p.id) e.currentTarget.style.background = 'white'; }}
    >
      <div style={{ fontSize: '11px', color: '#95a5a6', marginBottom: '2px' }}>
        {clientName(p.client_id)}{dlSort.field === 'pm' && ` · ${pmNameOf(p) || 'No PM'}`}
      </div>
      <div style={{ fontSize: '13px', fontWeight: '500', color: '#2c3e50', marginBottom: '4px' }}>{p.program_name}</div>
      <StatusPill value={p.status} lookups={statuses} fallback={STATUS_COLORS} />
    </div>
  );

  // The drilldown holds its own object, separate from the `programs` array the list
  // view renders — patch both, or the list shows a stale note until the next refetch.
  const saveNotes = async (value) => {
    const id = drilldown?.id ?? selectedProgram?.id;
    if (!id) return;
    const res = await axios.put(`${API}/programs/${id}`, { notes: value });
    setPrograms(prev => prev.map(p => (p.id === id ? { ...p, notes: res.data.notes } : p)));
    setDrilldown(prev => (prev ? { ...prev, notes: res.data.notes } : prev));
  };

  // Fetched only when Compact is opened, and refetched on every entry rather
  // than cached — one request, on an explicit navigation, always current. The
  // page load itself stays untouched. `isCurrent` lets the entry effect drop a
  // response that lands after the user has already left Compact.
  const loadRollup = (isCurrent = () => true) => {
    setRollupError(false);
    return axios.get(`${API}/programs/lead-summary`)
      .then(r => {
        if (!isCurrent()) return;
        const m = {};
        r.data.forEach(e => { m[e.program_id] = e; });
        setRollup(m);
        setRollupAt(new Date());
      })
      .catch(() => { if (isCurrent()) { setRollup(prev => prev || {}); setRollupError(true); } });
  };
  useEffect(() => {
    if (view !== 'compact') return;
    let cancelled = false;
    loadRollup(() => !cancelled);
    return () => { cancelled = true; };
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compact's 🔄 Refresh: the program list (status changes) and the lead counts
  // together. The current table stays on screen meanwhile — no flash to "Loading…".
  const refreshCompact = async () => {
    setRollupRefreshing(true);
    try { await Promise.all([fetchPrograms(), loadRollup()]); }
    finally { setRollupRefreshing(false); }
  };

  useEffect(() => {
    if (!isDrilldown) return;
    const measure = () => {
      const el = dlRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Whatever the app shell puts below us (its bottom padding) also has to fit,
      // or the page keeps a few pixels of scroll and nudges both panes.
      let below = 0;
      for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
        const cs = getComputedStyle(a);
        below += parseFloat(cs.paddingBottom) + parseFloat(cs.borderBottomWidth) + parseFloat(cs.marginBottom);
      }
      setDlHeight(Math.max(DL_MIN_HEIGHT, window.innerHeight - top - below - DL_BOTTOM_GAP));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isDrilldown, created]);

  // A newly picked program starts at its header, not wherever the last one was
  // scrolled to; and the selected row stays visible in the sidebar (matters for the
  // auto-selected one on load, which can sit below the fold of a long list).
  useEffect(() => {
    if (dlDetailRef.current) dlDetailRef.current.scrollTop = 0;
    const row = selectedProgram && dlListRef.current?.querySelector(`[data-program-id="${selectedProgram.id}"]`);
    if (row) row.scrollIntoView({ block: 'nearest' });
  }, [selectedProgram?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-expanding the sidebar brings the selected program back into view.
  useEffect(() => {
    if (sidebarCollapsed || !selectedProgram) return;
    dlListRef.current?.querySelector(`[data-program-id="${selectedProgram.id}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [sidebarCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDrilldownFor = (p) => { setView('drilldown'); loadDrilldown(p); };

  // Compact rows: active programs only. `activePrograms` is already everything
  // NOT in the lookup table's 'closed' status_group — today "Closed" and
  // "Closed – Invoiced" — so finished, invoiced business stays out of this view
  // entirely rather than sitting in a collapsed section. Worth knowing:
  // "Closed – Not Invoiced" is deliberately grouped as 'running' in
  // lookup_values (migration 0060), so those still appear here, which is right —
  // they still need invoicing. Classification stays data-driven; no status
  // string is matched in JS.
  const compactRows = sortCompactRows(activePrograms, compactSort, clientName, rollup);
  const compactSortProps = {
    field: compactSort.field,
    dir: compactSort.dir,
    onSort: (key) => setCompactSort(prev => (
      prev.field === key ? { field: key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field: key, dir: 'asc' }
    )),
  };

  // Land on a real program rather than an empty "select a program" panel. Always the
  // top row of the current sort, so the highlight can never end up stranded mid-list.
  // Waits on `statuses` as well as `programs` — the default sort ranks by the lookup
  // order, so selecting before those arrive would pick off the fallback ordering. The
  // ref makes it fire once per mount and never override a manual click afterwards.
  useEffect(() => {
    if (autoSelected.current || !isDrilldown || selectedProgram) return;
    if (programs.length === 0 || statuses.length === 0 || dlActive.length === 0) return;
    autoSelected.current = true;
    loadDrilldown(dlActive[0]);
  }, [programs, statuses, view, selectedProgram]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inline-edit option lists (list view)
  const clientOpts = clients.map(c => ({ value: c.id, label: c.name }));
  const managerOpts = [{ value: '', label: 'None' }, ...managers.map(m => ({ value: m.id, label: m.name }))];
  // Agencies are Clients too (Program.agency_id -> clients.id), so the same list serves both.
  const agencyOpts = [{ value: '', label: 'None' }, ...clients.map(c => ({ value: c.id, label: c.name }))];
  // Supporting picker omits whoever is currently the primary PM — the backend
  // enforces the same rule, this just keeps the impossible choice off screen.
  const supportingManagerOpts = managers
    .filter(m => String(m.id) !== String(form.program_manager_id))
    .map(m => ({ value: m.id, label: m.name }));
  const statusOpts = [{ value: '', label: '—' }, ...statuses.map(s => ({ value: s.value, label: s.value }))];

  // List-view columns (drilldown view is separate and unchanged)
  const listColumns = [
    { key: 'po_number', header: 'PO #', width: 100, render: p => <EditableCell value={p.po_number} onSave={v => saveField(p.id, { po_number: v })} /> },
    { key: 'program_name', header: 'Program Name', width: 200, render: p => <EditableCell value={p.program_name} display={<strong>{p.program_name || '(unnamed)'}</strong>} onSave={v => saveField(p.id, { program_name: v })} /> },
    { key: 'client', header: 'Client', width: 150, render: p => <EditableCell type="select" value={p.client_id} options={clientOpts} display={clientName(p.client_id)} onSave={v => saveField(p.id, { client_id: v ? parseInt(v) : null })} /> },
    { key: 'agency', header: 'Agency', width: 150, render: p => <EditableCell type="select" value={p.agency_id} options={agencyOpts} display={p.agency_id ? clientName(p.agency_id) : undefined} onSave={v => saveField(p.id, { agency_id: v ? parseInt(v) : null })} /> },
    { key: 'status', header: 'Status', width: 130, render: p => <EditableCell type="select" value={p.status} options={statusOpts} display={<StatusPill value={p.status} lookups={statuses} fallback={STATUS_COLORS} />} onSave={v => saveField(p.id, { status: v })} /> },
    { key: 'pm', header: 'PM', width: 130, render: p => <EditableCell type="select" value={p.program_manager_id} options={managerOpts} display={p.program_manager?.name || <span style={{ color: '#ccc' }}>—</span>} onSave={v => saveField(p.id, { program_manager_id: v ? parseInt(v) : null })} /> },
    // Read-only here: EditableCell edits one scalar, and a multi-select in a table
    // cell is more UI than this earns — assign supporting PMs in the edit form.
    { key: 'supporting_pms', header: 'Supporting PMs', width: 180, render: p => (
        (p.supporting_managers || []).length
          ? (p.supporting_managers || []).map(u => u.name).join(', ')
          : <span style={{ color: '#ccc' }}>—</span>
      ) },
    { key: 'order_date', header: 'Order Date', width: 140, render: p => <EditableCell type="date" value={p.order_date} display={fmt(p.order_date)} onSave={v => saveField(p.id, { order_date: v || null })} /> },
    { key: 'flight_date_start', header: 'Flight Start', width: 150, render: p => <EditableCell type="date" value={p.flight_date_start} display={fmt(p.flight_date_start)} onSave={v => saveField(p.id, { flight_date_start: v || null })} /> },
    { key: 'flight_date_end', header: 'Flight End', width: 150, render: p => <EditableCell type="date" value={p.flight_date_end} display={fmt(p.flight_date_end)} onSave={v => saveField(p.id, { flight_date_end: v || null })} /> },
    { key: 'total_revenue', header: 'Revenue', width: 120, render: p => <EditableCell type="number" step="0.01" value={p.total_revenue} display={fmtMoney(p.total_revenue)} onSave={v => saveField(p.id, { total_revenue: v === '' ? null : parseFloat(v) })} /> },
    { key: 'leads_ordered', header: 'Leads Ordered', width: 120, align: 'right', render: p => <EditableCell type="number" align="right" value={p.leads_ordered} display={p.leads_ordered != null ? p.leads_ordered.toLocaleString() : undefined} onSave={v => saveField(p.id, { leads_ordered: v === '' ? null : parseInt(v) })} /> },
    { key: 'payment_terms', header: 'Payment Terms', width: 140, render: p => <EditableCell value={p.payment_terms} onSave={v => saveField(p.id, { payment_terms: v })} /> },
    // Notes are free text and routinely long. The cell truncates to one line and
    // carries the full text as a tooltip; the table is tableLayout:'fixed', so
    // without the clamp a single long note would stretch the whole row.
    { key: 'notes', header: 'Notes', width: 220, render: p => (
        <EditableCell type="textarea" value={p.notes} emptyText="—"
          display={p.notes
            ? <span title={p.notes} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.notes}</span>
            : undefined}
          onSave={v => saveField(p.id, { notes: v })} /> ) },
    // The IO is a file, not a scalar, so it gets its own cell: open link,
    // attach/replace/remove (admin only, matching the API), whole cell a drop
    // target. Same component sits in the edit form below.
    { key: 'io_pdf', header: 'IO', width: 180, nowrap: true, render: p => (
        <IoPdfCell program={p} canEdit={isAdmin && !p.deleted_at} onChanged={replaceRow} />
      ) },
    { key: 'actions', header: 'Actions', width: 200, nowrap: true, render: p => p.deleted_at ? (
        <button onClick={() => restoreProgram(p)} style={{ padding: '4px 10px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Restore</button>
      ) : (
        <>
          <button onClick={() => handleEdit(p)} style={{ marginRight: '6px', padding: '4px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
          <button onClick={() => navigate(`/program-documents?program=${p.id}`)} title="Comms Brief and Work Order" style={{ marginRight: '6px', padding: '4px 10px', background: '#2e6bb5', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Docs</button>
          <button onClick={() => handleDelete(p)} style={{ padding: '4px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
        </>
      ) },
  ];

  return (
    <div>
      {created && (
        <div style={{ background: '#eafaf1', border: '1px solid #27ae60', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: '#1e6b43' }}>
            <strong>{created.name}</strong> was created
            {created.components ? ` with ${created.components} component${created.components === 1 ? '' : 's'}` : ''}
            {created.goals ? ` and ${created.goals} goal${created.goals === 1 ? '' : 's'}` : ''}.
            {!created.goals && ' It has no goals yet, so suppliers can\'t upload leads against it.'}
            {!created.hasIoPdf && ' No insertion order is on file.'}
            {' '}Set up QA checks and targeting in QA Rules — clone from a previous program, or connect existing lists.
          </span>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            {!created.goals && (
              <a href="/goals" style={{ padding: '5px 12px', background: '#fff', color: '#1e6b43', border: '1px solid #27ae60', borderRadius: '4px', fontSize: '12px', textDecoration: 'none' }}>Add Goals</a>
            )}
            <a href="/qa-rules" style={{ padding: '5px 12px', background: '#27ae60', color: 'white', borderRadius: '4px', fontSize: '12px', textDecoration: 'none' }}>Open QA Rules</a>
            <button onClick={() => setCreated(null)} style={{ padding: '5px 10px', background: 'none', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#555' }}>Dismiss</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Programs</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
<div style={{ background: '#f0f0f0', borderRadius: '6px', padding: '3px', display: 'flex' }}>
            {SWITCHER_VIEWS.map(([v, label, title]) => (
              <button key={v} onClick={() => setView(v)} title={title} style={segBtn(view === v)}>{label}</button>
            ))}
          </div>
          <ProgramFilters statuses={statuses} programs={programs}
            status={filterStatus} onStatusChange={setFilterStatus}
            pm={filterPm} onPmChange={setFilterPm} />
          {isAdmin && view === 'edit' && (
            <label style={{ fontSize: '13px', color: '#555', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
              <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
              Show deleted
            </label>
          )}
          {/* One entry point for both creation paths (typed, or prefilled from an IO
              PDF) — the choice is the wizard's first step. Admin-only, and no longer
              hidden behind the Edit view the way the old pair of buttons was. */}
          {view === 'compact' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <button onClick={refreshCompact} disabled={rollupRefreshing}
                title="Reload the program list and every program's lead counts"
                style={{ padding: '5px 12px', background: 'white', border: '1px solid #95a5a6', color: '#555', borderRadius: '4px', cursor: rollupRefreshing ? 'default' : 'pointer', fontSize: '12px', opacity: rollupRefreshing ? 0.6 : 1 }}>
                {rollupRefreshing ? 'Refreshing…' : '🔄 Refresh'}
              </button>
              {rollupAt && (
                <span style={{ fontSize: '11px', color: '#95a5a6', marginTop: '2px' }}>
                  Updated {rollupAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>
          )}
          {isAdmin && (
            <button onClick={() => navigate('/programs/new')} style={{ padding: '8px 16px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ New Program</button>
          )}
        </div>
      </div>

      {view === 'edit' && (
        <>
          {showForm && (
            <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 16px' }}>Edit Program</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                <FormField label="PO Number">
                  <input value={form.po_number} onChange={e => setForm({ ...form, po_number: e.target.value })} style={FORM_INPUT_STYLE} />
                </FormField>
                <FormField label="Program Name" required error={errors.program_name}>
                  <input value={form.program_name} onChange={e => setForm({ ...form, program_name: e.target.value })} style={FORM_INPUT_STYLE} />
                </FormField>
                <FormField label="Client" required error={errors.client_id}>
                  {/* No inline "+ New" here any more: creating a client is admin-only
                      (POST /clients/), and this edit form is reachable by PMs and
                      finance, who would only get a 403 from it. Client creation lives
                      on the Clients page and in the creation wizard. */}
                  <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} style={FORM_INPUT_STYLE}>
                    <option value="">Select client…</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Agency">
                  <select value={form.agency_id} onChange={e => setForm({ ...form, agency_id: e.target.value })} style={FORM_INPUT_STYLE}>
                    <option value="">None</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Status">
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={FORM_INPUT_STYLE}>
                    <option value="">Select…</option>
                    {statuses.map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
                  </select>
                </FormField>
                <FormField label="Program Manager">
                  <select value={form.program_manager_id} onChange={e => setForm({ ...form, program_manager_id: e.target.value })} style={FORM_INPUT_STYLE}>
                    <option value="">None</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Supporting Program Managers">
                  <MultiSelect
                    options={supportingManagerOpts}
                    values={form.supporting_manager_ids}
                    onChange={ids => setForm({ ...form, supporting_manager_ids: ids })}
                    placeholder="— none —"
                  />
                </FormField>
                <FormField label="Order Date" required error={errors.order_date}>
                  <input type="date" value={form.order_date} onChange={e => setForm({ ...form, order_date: e.target.value })} style={FORM_INPUT_STYLE} />
                </FormField>
                <FormField label="Flight Start" required error={errors.flight_date_start}>
                  <input type="date" value={form.flight_date_start} onChange={e => setForm({ ...form, flight_date_start: e.target.value })} style={FORM_INPUT_STYLE} />
                </FormField>
                <FormField label="Flight End" required error={errors.flight_date_end}>
                  <input type="date" value={form.flight_date_end} onChange={e => setForm({ ...form, flight_date_end: e.target.value })} style={FORM_INPUT_STYLE} />
                </FormField>
                <FormField label="Total Revenue">
                  <input type="number" step="0.01" value={form.total_revenue} onChange={e => setForm({ ...form, total_revenue: e.target.value })} style={FORM_INPUT_STYLE} />
                </FormField>
                <FormField label="Leads Ordered">
                  <input type="number" value={form.leads_ordered} onChange={e => setForm({ ...form, leads_ordered: e.target.value })} style={FORM_INPUT_STYLE} />
                </FormField>
                <FormField label="Payment Terms">
                  <input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} style={FORM_INPUT_STYLE} />
                </FormField>
                <FormField label="Notes" gridColumn="span 2">
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...FORM_INPUT_STYLE, height: '60px', resize: 'vertical' }} />
                </FormField>
                {/* Saves on its own (POST/DELETE /programs/{id}/io-pdf), not with Update —
                    the file lives in S3, and the form's PUT deliberately can't touch it. */}
                <FormField label="Insertion order" gridColumn="span 2">
                  <div style={{ ...FORM_INPUT_STYLE, display: 'flex', alignItems: 'center', height: 'auto', padding: '4px 6px' }}>
                    <IoPdfCell program={programs.find(p => p.id === editing) || { id: editing }}
                      canEdit={isAdmin} onChanged={replaceRow} style={{ flex: 1 }} />
                  </div>
                </FormField>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Update</button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm()); resetErrors(); }} style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          )}

          <DataTable
            rows={activePrograms}
            columns={listColumns}
            bulk={{ selected, toggle, toggleAll, statuses, onApply: bulkSetStatus }}
            rowStyle={p => ({ background: p.deleted_at ? '#f7f7f7' : (selected.has(p.id) ? '#f0f7ff' : 'white'), opacity: p.deleted_at ? 0.6 : 1 })}
            emptyText={anyFilter ? 'No programs match these filters.' : 'No programs yet.'}
          />
          {closedPrograms.length > 0 && (
            <CollapsibleSection label="Closed" count={closedPrograms.length} bodyStyle={{ padding: 0 }}>
              <DataTable
                rows={closedPrograms}
                columns={listColumns}
                bulk={{ selected, toggle, toggleAll, statuses, onApply: bulkSetStatus }}
                rowStyle={p => ({ background: p.deleted_at ? '#f7f7f7' : (selected.has(p.id) ? '#f0f7ff' : 'white'), opacity: p.deleted_at ? 0.6 : 1 })}
                emptyText="No closed programs."
              />
            </CollapsibleSection>
          )}
        </>
      )}

      {isDrilldown && (
        // Fixed to the window's height so each pane scrolls on its own; the page
        // itself only scrolls if the window is shorter than DL_MIN_HEIGHT.
        <div ref={dlRef} style={{ display: 'flex', gap: '20px', height: dlHeight ? `${dlHeight}px` : '600px' }}>
          {/* Left panel, collapsible to a thin rail for more detail room (remembered
              per browser). Title + sort row pinned, the program list scrolls below. */}
          {sidebarCollapsed ? (
            <div style={{ width: '34px', flexShrink: 0, background: 'white', border: '1px solid #e8ecef', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '8px', gap: '10px' }}>
              <button onClick={() => setSidebarCollapsed(false)} title="Show the program list" aria-label="Show the program list"
                style={{ width: '24px', height: '24px', padding: 0, background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', color: '#555', fontSize: '12px' }}>»</button>
              <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: '12px', fontWeight: '600', color: '#7f8c8d' }}>Programs</span>
            </div>
          ) : (
          <div style={{ width: '300px', flexShrink: 0, background: 'white', border: '1px solid #e8ecef', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '8px 10px 8px 16px', borderBottom: '1px solid #eee', background: '#f5f5f5', fontSize: '13px', fontWeight: '600', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Programs
              <button onClick={() => setSidebarCollapsed(true)} title="Hide the program list" aria-label="Hide the program list"
                style={{ width: '24px', height: '24px', padding: 0, background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', color: '#555', fontSize: '12px' }}>«</button>
            </div>
            <div style={{ padding: '6px 10px', borderBottom: '1px solid #eee', background: '#fafafa', display: 'flex', gap: '4px' }}>
              {[['status', 'Status', 'status'], ['client', 'Client', 'client'], ['pm', 'PM', 'program manager'], ['order_date', 'Date', 'order date']].map(([field, label, noun]) => (
                <button key={field} onClick={() => toggleDlSort(field)}
                  title={`Sort by ${noun}${dlSort.field === field ? ' (click to reverse)' : ''}`}
                  style={{
                    flex: 1, padding: '3px 4px', fontSize: '11px', cursor: 'pointer', borderRadius: '3px', whiteSpace: 'nowrap',
                    border: `1px solid ${dlSort.field === field ? '#3498db' : '#ddd'}`,
                    background: dlSort.field === field ? '#ebf5fb' : 'white',
                    color: dlSort.field === field ? '#2c7fb8' : '#666',
                    fontWeight: dlSort.field === field ? '600' : '400',
                  }}>
                  {label}{dlSort.field === field ? (dlSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </button>
              ))}
            </div>
            <div ref={dlListRef} style={{ flex: 1, overflowY: 'auto' }}>
              {dlActive.map(p => renderSidebarRow(p))}
              {closedPrograms.length > 0 && (
                <CollapsibleSection label="Closed" count={closedPrograms.length} bodyStyle={{ padding: 0 }}>
                  {dlClosed.map(p => renderSidebarRow(p))}
                </CollapsibleSection>
              )}
            </div>
          </div>
          )}

          {/* Right panel — scrolls on its own */}
          <div ref={dlDetailRef} style={{ flex: 1, minWidth: 0, overflowY: 'auto', background: 'white', border: '1px solid #e8ecef', borderRadius: '8px', padding: '20px' }}>
            {!selectedProgram && <div style={{ color: '#aaa', padding: '40px', textAlign: 'center' }}>Select a program to view details</div>}
            {selectedProgram && !drilldown && <div style={{ color: '#aaa', padding: '40px', textAlign: 'center' }}>Loading…</div>}
            {drilldown && <DrilldownPanel drilldown={drilldown} clientName={clientName} clientSalesPerson={clientSalesPerson} expandedComponents={expandedComponents} setExpandedComponents={setExpandedComponents} expandedGoals={expandedGoals} setExpandedGoals={setExpandedGoals} leadSummaries={leadSummaries} onRefresh={() => loadDrilldown(selectedProgram)} onEdit={() => { setView('edit'); handleEdit(selectedProgram); }} onSaveNotes={saveNotes} />}
          </div>
        </div>
      )}
      {view === 'compact' && (
        <>
          {rollupError && (
            <div style={{ padding: '10px 14px', marginBottom: '12px', background: '#fdedec', border: '1px solid #f5b7b1', borderRadius: '6px', color: '#c0392b', fontSize: '13px' }}>
              Couldn't load lead counts. The programs below are correct; their status columns are not.
            </div>
          )}
          {!rollup && !rollupError && (
            <div style={{ color: '#aaa', padding: '40px', textAlign: 'center' }}>Loading lead counts…</div>
          )}
          {rollup && (
            <CompactProgramTable rows={compactRows} rollup={rollup} clientName={clientName}
              statuses={statuses} onSelect={openDrilldownFor} sort={compactSortProps}
              emptyText={anyFilter ? 'No open programs match these filters.' : 'No open programs.'} />
          )}
        </>
      )}
      {deleteModal && (
        <ReparentDeleteModal
          entityName={deleteModal.program_name || `Program #${deleteModal.id}`}
          parentNoun="program"
          childNoun="components"
          items={(deleteModal.components || []).map(c => ({ id: c.id, name: c.component_name || `Component #${c.id}` }))}
          targets={programs.filter(x => x.id !== deleteModal.id).map(x => ({ value: x.id, label: x.program_name || `Program #${x.id}` }))}
          childIdKey="component_id"
          targetIdKey="target_program_id"
          onCascade={doCascadeDelete}
          onMove={doReparentDelete}
          onClose={() => setDeleteModal(null)}
          busy={deleteBusy}
        />
      )}
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}




// Exported for Programs.drilldown.test.js.
export function DrilldownPanel({ drilldown: d, clientName, clientSalesPerson, expandedComponents, setExpandedComponents, expandedGoals, setExpandedGoals, leadSummaries, onRefresh, onEdit, onSaveNotes }) {
  // includes_call_review is component-level now — the table below is one shared table
  // for every component/goal row, so its column set has to be the union across all of
  // this program's components (a component without the flag just shows 0/— there).
  const statusCols = getLeadStatuses((d.components || []).some(c => c.includes_call_review));
  const sectionStyle = { background: '#f9f9f9', border: '1px solid #eee', borderRadius: '6px', padding: '16px', marginBottom: '16px' };
  const notesTextStyle = { fontSize: '13px', fontWeight: '600', color: '#2c3e50', lineHeight: '1.5' };
  // Notes show 3 lines with a Show more toggle; the toggle only appears when the
  // text actually overflows. Collapses again whenever another program is opened.
  const notesRef = useRef(null);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [notesOverflow, setNotesOverflow] = useState(false);
  useEffect(() => { setNotesExpanded(false); }, [d.id]);
  useLayoutEffect(() => {
    const el = notesRef.current;
    if (el && !notesExpanded) setNotesOverflow(el.scrollHeight > el.clientHeight + 1);
  }, [notesExpanded, d.id, d.notes]);
  const fieldRow = (label, value) => (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', fontSize: '13px' }}>
      <span style={{ color: '#7f8c8d', minWidth: '140px' }}>{label}</span>
      <span style={{ color: '#2c3e50' }}>{value || '—'}</span>
    </div>
  );

  // Aggregate lead summary across all goals in a component
  const compLeadAggregate = (comp) => {
    const agg = { total: 0, by_status: {} };
    (comp.goals || []).forEach(g => {
      const gs = leadSummaries[g.id];
      if (!gs) return;
      agg.total += gs.total || 0;
      Object.entries(gs.by_status || {}).forEach(([s, c]) => {
        agg.by_status[s] = (agg.by_status[s] || 0) + c;
      });
    });
    return agg;
  };

  // Program-level reconciliation — the tier above the per-component goal check: every
  // component's QTY should add up to the program's own Leads Ordered. Worth surfacing
  // precisely because, unlike the goals-vs-component check, this one has a real live
  // mismatch in production (a program whose replacement components were never added to
  // its ordered figure). Nothing here is special-cased — a genuine gap is meant to show.
  const componentsTotal = (d.components || []).reduce((n, c) => n + (c.quantity_ordered || 0), 0);
  const orderedTotal = d.leads_ordered ?? null;
  const totalMismatch = orderedTotal !== null && componentsTotal > 0 && componentsTotal !== orderedTotal;
  const programTotals = (d.components || []).reduce((acc, comp) => {
    Object.entries(compLeadAggregate(comp).by_status).forEach(([s, c]) => {
      acc[s] = (acc[s] || 0) + c;
    });
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, color: '#2c3e50' }}>{d.program_name}</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          {d.io_pdf_filename && (
            <button onClick={() => openIoPdf(d.id)} title={`Open the original insertion order (${d.io_pdf_filename})`}
              style={{ padding: '5px 12px', background: 'white', border: '1px solid #8e44ad', color: '#8e44ad', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
              📄 View IO PDF
            </button>
          )}
          {onRefresh && (
            <button onClick={onRefresh} title="Reload lead counts — useful after accepting a supplier delivery or other QA changes made elsewhere"
              style={{ padding: '5px 12px', background: 'white', border: '1px solid #95a5a6', color: '#555', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
              🔄 Refresh
            </button>
          )}
          {onEdit && (
            <button onClick={onEdit} title="Switch to the list view with this program's edit form already open"
              style={{ padding: '5px 12px', background: 'white', border: '1px solid #3498db', color: '#2c7fb8', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
              ✎ Edit
            </button>
          )}
        </div>
      </div>

      {/* Program info */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: '600', marginBottom: '12px', color: '#555', fontSize: '14px' }}>Program Info</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
          {fieldRow('PO Number', d.po_number)}
          {fieldRow('Status', d.status)}
          {fieldRow('Client', clientName(d.client_id))}
          {fieldRow('Agency', d.agency_id ? clientName(d.agency_id) : null)}
          {fieldRow('Salesperson', clientSalesPerson(d.client_id))}
          {fieldRow('Program Manager', d.program_manager?.name)}
          {fieldRow('Supporting PMs', (d.supporting_managers || []).map(u => u.name).join(', ') || null)}
          {fieldRow('Order Date', d.order_date)}
          {fieldRow('Flight Start', d.flight_date_start)}
          {fieldRow('Flight End', d.flight_date_end)}
          {fieldRow('Revenue', d.total_revenue ? '$' + Number(d.total_revenue).toLocaleString() : null)}
          {fieldRow('Leads Ordered', d.leads_ordered)}
          {fieldRow('Payment Terms', d.payment_terms)}
        </div>
        {/* Rendered even when empty now that it's editable — a program with no notes
            still needs somewhere to click to write the first one. */}
        <div style={{ marginTop: '12px', padding: '10px 12px', background: 'white', border: '1px solid #eee', borderLeft: '3px solid #e67e22', borderRadius: '4px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#e67e22', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>Notes</div>
          {onSaveNotes ? (
            <EditableCell
              type="textarea" rows={8} value={d.notes} onSave={onSaveNotes}
              placeholder="Enter to add a line, Esc to cancel" emptyText="Click to add a note"
              display={d.notes
                ? <span ref={notesRef} style={{ ...notesTextStyle, ...(notesExpanded ? {} : NOTES_CLAMP) }}>{d.notes}</span>
                : undefined}
            />
          ) : (
            <div ref={notesRef} style={{ ...notesTextStyle, ...(notesExpanded ? {} : NOTES_CLAMP) }}>{d.notes}</div>
          )}
          {/* Outside the EditableCell so toggling doesn't also open the editor. */}
          {d.notes && (notesOverflow || notesExpanded) && (
            <button onClick={() => setNotesExpanded(x => !x)}
              style={{ marginTop: '4px', padding: 0, background: 'none', border: 'none', color: '#2c7fb8', cursor: 'pointer', fontSize: '12px' }}>
              {notesExpanded ? 'Show less ▴' : 'Show more ▾'}
            </button>
          )}
        </div>
      </div>

      {/* Components spreadsheet */}
      <div style={{ fontWeight: '600', marginBottom: '10px', color: '#555', fontSize: '14px' }}>Components ({(d.components || []).length})</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', minWidth: '200px' }}>Component / Goal</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' }}>QTY Ordered</th>
              {statusCols.map(s => (
                <th key={s} style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' }}>
                  <span style={{ background: LEAD_STATUS_COLORS[s]?.color, color: 'white', borderRadius: '3px', padding: '2px 7px', fontSize: '11px' }}>{s}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(d.components || []).flatMap(comp => {
              const isExpanded = expandedComponents[comp.id];
              const compQty = comp.quantity_ordered || 0;
              const goalSum = (comp.goals || []).reduce((n, g) => n + (g.quantity_ordered || 0), 0);
              const agg = compLeadAggregate(comp);
              const goalCount = (comp.goals || []).length;
              const sumMismatch = compQty > 0 && goalSum > 0 && goalSum !== compQty;

              const rows = [];

              rows.push(
                <tr key={`comp-${comp.id}`}
                  onClick={() => setExpandedComponents(prev => ({ ...prev, [comp.id]: !prev[comp.id] }))}
                  style={{ background: '#f0f4f8', cursor: 'pointer', borderBottom: '1px solid #d5dde8' }}
                >
                  <td style={{ padding: '10px 12px', fontWeight: '600', color: '#2c3e50' }}>
                    <span style={{ marginRight: '8px', color: '#888', fontSize: '11px' }}>{isExpanded ? '▼' : '▶'}</span>
                    {comp.component_name || '(unnamed)'}
                    <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888', fontWeight: '400' }}>
                      {goalCount} goal{goalCount !== 1 ? 's' : ''}
                    </span>
                    {comp.includes_call_review && (
                      <span title="Includes Call Review" style={{ marginLeft: '8px', background: LEAD_STATUS_COLORS['Approved Pending Call Review']?.bg, color: LEAD_STATUS_COLORS['Approved Pending Call Review']?.color, borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: '600' }}>
                        Call Review
                      </span>
                    )}
                  </td>
                  {/* The Goal Sum column was dropped — with goals now always expanded
                      the individual quantities are on screen anyway, and it had never
                      once disagreed in production. The cross-check itself is kept, as
                      a ⚠ on the component's own QTY whenever the two actually differ. */}
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: sumMismatch ? '#e67e22' : undefined }}>
                    {compQty || '—'}
                    {sumMismatch && (
                      <span title={`This component's goals add up to ${goalSum}, which doesn't match its QTY Ordered of ${compQty}`} style={{ marginLeft: '4px' }}>⚠</span>
                    )}
                  </td>
                  {statusCols.map(s => (
                    <td key={s} style={{ padding: '10px 12px', textAlign: 'right' }}>
                      {agg.by_status[s] ? (
                        <span style={{ background: LEAD_STATUS_COLORS[s]?.color, color: 'white', borderRadius: '3px', padding: '1px 7px', fontSize: '11px' }}>
                          {agg.by_status[s]}
                        </span>
                      ) : <span style={{ color: '#ddd', fontSize: '11px' }}>—</span>}
                    </td>
                  ))}
                </tr>
              );

              // Component notes are deliberately NOT rendered here. They hold the
              // IO's contract terms (rate type, billing, targeting splits) plus the
              // import wizard's "Targeting from IO — …" line — reference material,
              // not at-a-glance status, and long enough to bury the lead counts this
              // table exists to show. Still fully visible/editable on Components.js.

              if (isExpanded) {
                (comp.goals || []).forEach(goal => {
                  const goalLeads = leadSummaries[goal.id];
                  rows.push(
                    <tr key={`goal-${goal.id}`} style={{ background: 'white', borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px 12px 8px 36px', color: '#34495e', fontSize: '12px' }}>
                        {goal.goal_name || '(unnamed)'}
                        {goal.status && (
                          <span style={{ background: '#eee', color: '#555', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', marginLeft: '8px' }}>
                            {goal.status}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', color: '#34495e' }}>
                        {goal.quantity_ordered ?? '—'}
                      </td>
                      {statusCols.map(s => (
                        <td key={s} style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px' }}>
                          {goalLeads?.by_status?.[s] ? (
                            <span style={{ background: LEAD_STATUS_COLORS[s]?.color, color: 'white', borderRadius: '3px', padding: '1px 7px', fontSize: '11px' }}>
                              {goalLeads.by_status[s]}
                            </span>
                          ) : <span style={{ color: '#ddd', fontSize: '11px' }}>—</span>}
                        </td>
                      ))}
                    </tr>
                  );
                });
              }

              return rows;
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: '#eef2f6', borderTop: '2px solid #d5dde8' }}>
              <td style={{ padding: '10px 12px', fontWeight: '600', color: '#2c3e50' }}>
                All components
                {totalMismatch && (
                  <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: '600', color: '#e67e22' }}>
                    ⚠ Leads Ordered is {orderedTotal.toLocaleString()} ({componentsTotal > orderedTotal ? '+' : ''}{(componentsTotal - orderedTotal).toLocaleString()})
                  </span>
                )}
              </td>
              <td
                title={orderedTotal === null ? 'This program has no Leads Ordered set, so there is nothing to reconcile against' : `Program Leads Ordered: ${orderedTotal.toLocaleString()}`}
                style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: totalMismatch ? '#e67e22' : '#2c3e50' }}>
                {componentsTotal || '—'}
              </td>
              {statusCols.map(s => (
                <td key={s} style={{ padding: '10px 12px', textAlign: 'right' }}>
                  {programTotals[s] ? (
                    <span style={{ background: LEAD_STATUS_COLORS[s]?.color, color: 'white', borderRadius: '3px', padding: '1px 7px', fontSize: '11px', fontWeight: '600' }}>
                      {programTotals[s]}
                    </span>
                  ) : <span style={{ color: '#ddd', fontSize: '11px' }}>—</span>}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// Compact view — every program on one screen with its lead counts by status,
// rolled up server-side by GET /programs/lead-summary.
//
// Built on the shared DataTable rather than a bespoke <table>, so it inherits
// resizable columns, sortable headers, the empty state and the totals row that
// every other list on this app already uses.
//
// There is no "Delivered" column: the six status columns are exhaustive, so a
// total would just be their sum. All six are always shown, call review
// included — showing that bucket conditionally would drop leads out of the row
// silently on exactly the programs that have them.
// Pure, so the ordering is testable on its own. `rows` should already be the
// active-only list — closed-and-invoiced programs are excluded upstream by
// status_group, not here.
// Compact's Flight end cell: "Dec 7", with the year only when it isn't this one
// ("Dec 7, 2027"). Parsed as a local date so it never prints a day early.
export function fmtFlightEnd(iso, today = new Date()) {
  const d = parseLocalDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== today.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts);
}

// Compact's lead-status column widths: room for each heading on one line at
// the dense table's sizes. Approved Pending Call Review wraps to two lines.
const COMPACT_STATUS_WIDTHS = {
  'Pending Review': 116,
  'Under Review': 106,
  'Approved Pending Call Review': 134,
  'DemandArc Accepted': 140,
  'Client Accepted': 124,
  'Rejected': 86,
};

export function sortCompactRows(rows, sort, clientName, rollup) {
  const value = (p) => {
    switch (sort.field) {
      case 'client':        return (clientName(p.client_id) || '').toLowerCase();
      case 'program_name':  return (p.program_name || '').toLowerCase();
      case 'status':        return (p.status || '').toLowerCase();
      case 'leads_ordered': return p.leads_ordered || 0;
      case 'flight_date_end': return p.flight_date_end || null;
      default:              return (rollup && rollup[p.id]?.by_status?.[sort.field]) || 0;
    }
  };
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...(rows || [])].sort((a, b) => {
    const av = value(a), bv = value(b);
    if (av === null && bv !== null) return 1;      // a missing end date sinks, either direction
    if (bv === null && av !== null) return -1;
    if (av < bv) return -dir;
    if (av > bv) return dir;
    // Equal keys keep a stable, predictable order rather than whatever the
    // previous sort happened to leave behind.
    return (a.program_name || '').localeCompare(b.program_name || '');
  });
}

export function CompactProgramTable({ rows, rollup, clientName, statuses, onSelect, sort, emptyText }) {
  const statusCols = getLeadStatuses(true);
  const sumOf = (rs, st) => rs.reduce((n, p) => n + (rollup[p.id]?.by_status?.[st] || 0), 0);
  const chip = (st, n, bold) => (
    n ? (
      <span style={{ background: LEAD_STATUS_COLORS[st]?.color, color: 'white', borderRadius: '3px', padding: '1px 7px', fontSize: '11px', fontWeight: bold ? '600' : '400' }}>
        {n.toLocaleString()}
      </span>
    ) : <span style={{ color: '#ddd', fontSize: '11px' }}>—</span>
  );

  const columns = [
    // Company rides above the program name as a small eyebrow (as in the Drilldown
    // sidebar) instead of its own column, and Program → Ordered are frozen so the
    // lead-status columns slide under them (Edward, 2026-11-04). Sorting this
    // column groups by company, then program name. The table is `dense` and each
    // program is one line (full name on hover), so a screen holds more rows.
    { key: 'client', header: 'Program', width: 300, sortable: true, frozen: true,
      render: p => (
        <>
          <div style={{ fontSize: '10.5px', color: '#95a5a6', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clientName(p.client_id)}</div>
          <div title={p.program_name || undefined} style={{ fontWeight: '500', color: '#2c3e50', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.program_name || '(unnamed)'}</div>
        </>
      ),
      footer: rs => `All programs (${rs.length})` },
    { key: 'status', header: 'Status', width: 120, sortable: true, frozen: true,
      render: p => <StatusPill value={p.status} lookups={statuses} fallback={STATUS_COLORS} /> },
    { key: 'flight_date_end', header: 'Flight end', width: 82, sortable: true, nowrap: true, frozen: true,
      render: p => (p.flight_date_end
        ? <span title={`Flight ends ${p.flight_date_end}`} style={{ color: '#2c3e50' }}>{fmtFlightEnd(p.flight_date_end)}</span>
        : <span style={{ color: '#ddd' }}>—</span>) },
    { key: 'leads_ordered', header: 'Ordered', width: 76, align: 'right', sortable: true, frozen: true,
      render: p => (p.leads_ordered ? p.leads_ordered.toLocaleString() : <span style={{ color: '#ddd' }}>—</span>),
      footer: rs => rs.reduce((n, p) => n + (p.leads_ordered || 0), 0).toLocaleString() },
    ...statusCols.map(st => ({
      // Each status column is just wide enough for its heading on one line
      // (Edward, 2026-11-04); only the long "Approved Pending Call Review" is
      // allowed to wrap onto two.
      key: st, width: COMPACT_STATUS_WIDTHS[st] || 112, align: 'right',
      header: <span title={st} style={{ display: 'inline-block', whiteSpace: st === 'Approved Pending Call Review' ? 'normal' : 'nowrap', lineHeight: 1.2, textAlign: 'center', background: LEAD_STATUS_COLORS[st]?.color, color: 'white', borderRadius: '3px', padding: '2px 6px', fontSize: '10.5px' }}>{st}</span>,
      render: p => chip(st, rollup[p.id]?.by_status?.[st], false),
      footer: rs => chip(st, sumOf(rs, st), true),
    })),
  ];

  return (
    <DataTable rows={rows} columns={columns} sort={sort} onRowClick={onSelect} dense
      emptyText={emptyText || 'No programs.'} />
  );
}
